import { AlertTriangle, Sparkles } from "lucide-react";
import type {
  BuyerImportListingInterestDto,
  BuyerSmartImportAnalysisDto,
  BuyerSmartImportPreviewDto,
  BuyerSmartImportReviewStateDto,
} from "@/api/buyerSmartImport";
import type { BuyerLeadSource } from "@/api/buyers";
import { Button, FormField, Input, Skeleton } from "@/design-system/components/ui";
import { aiFallbackBanner, planOriginLabel, smartImportCopy } from "./smartImportCopy";
import { SmartImportCounts, countsReconcile } from "./SmartImportCounts";
import { SmartImportInterestsTable } from "./SmartImportInterestsTable";
import { SmartImportPreview } from "./SmartImportPreview";
import { SmartImportTabPanel } from "./SmartImportTabPanel";
import { cutoffIsAvailable, retargetColumn } from "./smartImportReview";

/**
 * The review screen (D4): the whole mapping, what it will do, and what it will
 * create — and nothing is written until the operator says so.
 *
 * <p>🔴 <b>Every count and every previewed buyer on this screen comes from a
 * SERVER dry-run</b>, re-run on every change to the review state. The client
 * never computes what an import will do; it is the same code path the commit
 * runs, which is the only way "Import all commits exactly what the preview
 * showed" can be true rather than hoped.</p>
 *
 * <p>Presentational by design — the queries live on the page, so this renders
 * against fixtures with no hooks to mock and no network to stub.</p>
 */

export interface SmartImportReviewProps {
  analysis: BuyerSmartImportAnalysisDto;
  review: BuyerSmartImportReviewStateDto;
  onReview: (review: BuyerSmartImportReviewStateDto) => void;

  /** The dry-run. */
  preview?: BuyerSmartImportPreviewDto | null;
  previewLoading?: boolean;
  previewError?: string | null;

  /** The interests call (D8). */
  interests: readonly BuyerImportListingInterestDto[];
  interestsLoading?: boolean;
  interestsFailureMessage?: string | null;
  onInterests: (interests: BuyerImportListingInterestDto[]) => void;

  /** Offered whenever the plan did not come from the model (D19). */
  onRemapWithAi?: () => void;
  remapping?: boolean;

  onCommit: () => void;
  committing?: boolean;
  commitError?: string | null;
}

export function SmartImportReview({
  analysis,
  review,
  onReview,
  preview,
  previewLoading,
  previewError,
  interests,
  interestsLoading,
  interestsFailureMessage,
  onInterests,
  onRemapWithAi,
  remapping,
  onCommit,
  committing,
  commitError,
}: SmartImportReviewProps) {
  const summaries = new Map(analysis.tabs.map((t) => [t.tabName, t]));
  const cutoffAvailable = cutoffIsAvailable(review);

  const setTab = (
    tabName: string,
    change: (tab: BuyerSmartImportReviewStateDto["tabs"][number]) => BuyerSmartImportReviewStateDto["tabs"][number],
  ): void =>
    onReview({ ...review, tabs: review.tabs.map((t) => (t.tabName === tabName ? change(t) : t)) });

  const setColumn = (
    tabName: string,
    columnIndex: number,
    change: (
      column: BuyerSmartImportReviewStateDto["tabs"][number]["columns"][number],
    ) => BuyerSmartImportReviewStateDto["tabs"][number]["columns"][number],
  ): void =>
    setTab(tabName, (t) => ({
      ...t,
      columns: t.columns.map((c) => (c.index === columnIndex ? change(c) : c)),
    }));

  /*
    🔴 The commit is refused while the dry-run is stale, in flight, failed, or
    reporting counts that do not reconcile. "Import all commits exactly what
    the last preview showed" is only a guarantee while there IS a last preview
    and it describes the review state on screen — a button that stayed live
    across a re-run would commit against a preview the operator never saw.
  */
  const canCommit =
    preview != null &&
    !previewLoading &&
    !previewError &&
    !committing &&
    countsReconcile(preview.counts) &&
    review.tabs.some((t) => t.include);

  return (
    <div className="grid gap-space-5" data-testid="smart-import-review">
      <section className="grid gap-space-2" aria-label={smartImportCopy.reviewTitle}>
        <div className="flex flex-wrap items-baseline gap-space-2">
          <h2 className="font-display font-semibold text-lead text-onyx">
            {smartImportCopy.reviewTitle}
          </h2>
          <span className="text-small text-gray-mid" data-testid="smart-import-plan-origin">
            {analysis.fileName} — {planOriginLabel(analysis.planOrigin, analysis.reusedFromBatchAt)}
          </span>
          {analysis.planOrigin !== "ai" && onRemapWithAi && (
            <Button
              variant="text"
              size="sm"
              data-testid="smart-import-remap-with-ai"
              disabled={remapping}
              onClick={onRemapWithAi}
            >
              <Sparkles size={14} strokeWidth={1.5} aria-hidden />
              {smartImportCopy.remapWithAi}
            </Button>
          )}
        </div>

        {analysis.aiFailureMessage && (
          <p
            className="flex items-start gap-space-2 text-small text-status-warning-ink"
            data-testid="smart-import-ai-fallback"
          >
            <AlertTriangle size={14} strokeWidth={1.5} aria-hidden className="mt-[2px]" />
            <span>{aiFallbackBanner(analysis.aiFailureMessage)}</span>
          </p>
        )}

        {analysis.warnings.length > 0 && (
          <ul
            className="grid gap-space-1 text-small text-status-warning-ink"
            data-testid="smart-import-plan-warnings"
          >
            {analysis.warnings.map((w, i) => (
              <li key={`plan-warning-${i}`} className="flex items-start gap-space-2">
                <AlertTriangle size={14} strokeWidth={1.5} aria-hidden className="mt-[2px]" />
                <span>{w}</span>
              </li>
            ))}
          </ul>
        )}
      </section>

      <div className="grid gap-space-3">
        {review.tabs.map((tab) => (
          <SmartImportTabPanel
            key={tab.tabName}
            tab={tab}
            summary={summaries.get(tab.tabName)}
            disabled={committing}
            onToggleInclude={(include) => setTab(tab.tabName, (t) => ({ ...t, include }))}
            onChangeHeaderRow={(headerRowIndex) =>
              setTab(tab.tabName, (t) => ({ ...t, headerRowIndex }))
            }
            onChangeSource={(source: BuyerLeadSource) =>
              setTab(tab.tabName, (t) => ({ ...t, source }))
            }
            onChangeSourceDetail={(sourceDetail) =>
              setTab(tab.tabName, (t) => ({ ...t, sourceDetail }))
            }
            /*
              Through `retargetColumn`, not a local assignment: re-pointing a
              column RE-PICKS its transform when the one it carries is no
              longer legal for the new target, and that rule has to be the one
              the review module's own tests pin rather than a second copy here.
            */
            onChangeTarget={(columnIndex, target) =>
              onReview(retargetColumn(review, tab.tabName, columnIndex, target))
            }
            onChangeTransform={(columnIndex, transform) =>
              setColumn(tab.tabName, columnIndex, (c) => ({ ...c, transform }))
            }
            onChangeSentinels={(columnIndex, sentinels) =>
              setColumn(tab.tabName, columnIndex, (c) => ({ ...c, sentinels }))
            }
            onChangeFormat={(columnIndex, format) =>
              setColumn(tab.tabName, columnIndex, (c) => ({ ...c, format }))
            }
          />
        ))}
      </div>

      <SmartImportInterestsTable
        interests={interests}
        enabled={review.inferInterests}
        disabled={committing}
        loading={interestsLoading}
        aiFailureMessage={interestsFailureMessage}
        onToggle={(inferInterests) => onReview({ ...review, inferInterests })}
        onChange={onInterests}
      />

      {/*
        D12 — the cutoff is offered ONLY when a received-date column is mapped.
        Without one there is nothing to compare a date against, so the control
        would be a picker that silently did nothing to the import.
      */}
      {cutoffAvailable && (
        <section aria-label={smartImportCopy.cutoffLabel} data-testid="smart-import-cutoff">
          <FormField label={smartImportCopy.cutoffLabel} helperText={smartImportCopy.cutoffHint}>
            <Input
              type="date"
              data-testid="smart-import-cutoff-input"
              disabled={committing}
              value={review.cutoff ?? ""}
              onChange={(e) => onReview({ ...review, cutoff: e.target.value || null })}
            />
          </FormField>
          {review.cutoff && (
            <Button
              variant="text"
              size="sm"
              data-testid="smart-import-cutoff-clear"
              disabled={committing}
              onClick={() => onReview({ ...review, cutoff: null })}
            >
              {smartImportCopy.cutoffClear}
            </Button>
          )}
        </section>
      )}

      {previewError && (
        <p
          role="alert"
          className="flex items-start gap-space-2 text-small text-status-error"
          data-testid="smart-import-preview-error"
        >
          <AlertTriangle size={14} strokeWidth={1.5} aria-hidden className="mt-[2px]" />
          <span>{previewError}</span>
        </p>
      )}

      {previewLoading && !preview && (
        <div data-testid="smart-import-preview-loading">
          <Skeleton className="h-32 w-full" />
        </div>
      )}

      {preview && (
        <>
          <SmartImportCounts preview={preview} />
          <SmartImportPreview preview={preview} sourceName={analysis.fileName} />
        </>
      )}

      {commitError && (
        <p
          role="alert"
          className="flex items-start gap-space-2 text-small text-status-error"
          data-testid="smart-import-commit-error"
        >
          <AlertTriangle size={14} strokeWidth={1.5} aria-hidden className="mt-[2px]" />
          <span>{commitError}</span>
        </p>
      )}

      <div className="grid gap-space-2">
        <Button data-testid="smart-import-commit" disabled={!canCommit} onClick={onCommit}>
          {smartImportCopy.commitAction}
        </Button>
        <p className="text-caption text-gray-mid">{smartImportCopy.commitHint}</p>
      </div>
    </div>
  );
}
